import type { WalkinActivity, WalkinLeadMatch } from './types';
import { LEAD_SOURCE_OPTIONS } from './types';

/**
 * AdoraCare - Existing-lead match + prior-activity panel (S2-P5A2
 * walk-in-as-lead-source, BUILD-01).
 *
 * Pure presentational component. Renders, for a phone the receptionist
 * just searched:
 *   - prior AdoraCare activity (walk-in / check-in COUNT + last-activity
 *     date) for a confirmed returning visitor, and
 *   - any existing ACTIVE leads for that phone.
 *
 * DISPLAY ONLY. This slice takes NO action on a match: there is no
 * link/continue/create affordance (that is S2-P5A3). Multiple leads are
 * all shown as a human-review list; the UI never auto-selects one.
 *
 * Vocabulary: lead / returning visitor / walk-in - never "patient"
 * ("patient" is reserved for the EHNote boundary package). No phone,
 * fingerprint, doctor, or clinical/visit-detail content is rendered.
 */

interface ExistingLeadMatchesProps {
  leads: WalkinLeadMatch[];
  truncated: boolean;
  activity: WalkinActivity | null;
  /**
   * S2-P5A3: confirm-action callback. Provided ONLY when
   * WALKIN_LEAD_LINK_V1_ENABLED is on (FormScreen passes it conditionally).
   * When present, each SAME-CLINIC lead renders a "Use this lead" button
   * that links this visit to that existing lead. When ABSENT (flag off),
   * the panel stays display-only -- A2 behavior, byte-identical -- and the
   * "Review only" note shows. Nothing ever auto-selects.
   */
  onUseLead?: (lead: WalkinLeadMatch) => void;
  /** True while a link/create confirm is in flight (disables the buttons). */
  actionPending?: boolean;
}

/** Render the date portion (YYYY-MM-DD) of an ISO timestamp, safely. */
function isoDate(iso: string | null): string | null {
  if (typeof iso !== 'string' || iso.length < 10) return null;
  const datePart = iso.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * Pretty "D Mon YYYY" from an ISO date (matching the returning-patient card),
 * or null when unparseable. Display only -- the lead's createdAt is shown IF it
 * is already present on the match; nothing is fetched.
 */
function formatCreated(iso: string | null): string | null {
  const date = isoDate(iso);
  if (date === null) return null;
  const monthIndex = Number(date.slice(5, 7)) - 1;
  if (monthIndex < 0 || monthIndex > 11) return null;
  return `${Number(date.slice(8, 10))} ${MONTHS[monthIndex]} ${date.slice(0, 4)}`;
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/**
 * Title-case a snake_case control token for display:
 *   'organic_search' -> 'Organic search', 'Open' -> 'Open'.
 * Used as the fallback when a token has no curated label.
 */
export function humanizeToken(token: string): string {
  if (token.length === 0) return '';
  const spaced = token.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Humanize a lead's source token for display. Prefers the shared
 * LEAD_SOURCE_OPTIONS vocabulary (so 'walk_in' -> 'Walk-in',
 * 'paid_search' -> 'Paid search') and falls back to a generic
 * title-case for any token outside that list. Display only -- the
 * raw token is never mutated, sent, or stored.
 *
 * Exported so the Recent walk-ins rail reuses the SAME mapping
 * (S2-P5E-B2 polish) rather than rendering raw enum tokens.
 */
export function humanizeSource(token: string): string {
  const known = LEAD_SOURCE_OPTIONS.find((option) => option.id === token);
  return known ? known.label : humanizeToken(token);
}

export default function ExistingLeadMatches({
  leads,
  truncated,
  activity,
  onUseLead,
  actionPending = false,
}: ExistingLeadMatchesProps) {
  // Confirm actions render ONLY when the flag-gated callback is supplied.
  const linkEnabled = typeof onUseLead === 'function';
  const hasActivity =
    activity !== null &&
    (activity.walkinCount > 0 ||
      activity.checkinCount > 0 ||
      activity.lastActivityAt !== null);
  const hasLeads = leads.length > 0;

  if (!hasActivity && !hasLeads) {
    return null;
  }

  const lastDate = activity ? isoDate(activity.lastActivityAt) : null;

  return (
    <div
      className="lead-results"
      role="region"
      aria-live="polite"
      aria-label="Existing lead and activity results"
    >
      {hasActivity && activity && (
        <div className="lead-activity" role="note">
          <span className="lead-activity-label">Prior AdoraCare activity:</span>{' '}
          {plural(activity.walkinCount, 'walk-in')},{' '}
          {plural(activity.checkinCount, 'check-in')}.
          {lastDate !== null && <> Last activity {lastDate}.</>}
        </div>
      )}

      {hasLeads && (
        <>
          <div className="lead-results-heading">
            Existing lead{leads.length === 1 ? '' : 's'} for this number
          </div>
          {truncated && (
            <div className="lead-truncated" role="note">
              More leads may exist. Review carefully.
            </div>
          )}
          {leads.map((lead) => (
            <div key={lead.leadId} className="lead-card">
              <div className="lead-card-fields">
                <div className="lead-card-field">
                  <span className="lead-card-field-label">Name</span>
                  <span className="lead-card-field-value lead-card-name">
                    {lead.leadName.length > 0 ? lead.leadName : 'Unnamed lead'}
                  </span>
                </div>
                <div className="lead-card-field">
                  <span className="lead-card-field-label">Source</span>
                  <span className="lead-card-field-value lead-card__pill lead-card__pill--source">
                    {humanizeSource(lead.source)}
                  </span>
                </div>
                <div className="lead-card-field">
                  <span className="lead-card-field-label">Status</span>
                  <span className="lead-card-field-value lead-card__pill lead-card__pill--status">
                    {humanizeToken(lead.caseStatus)}
                  </span>
                </div>
                <div className="lead-card-field">
                  <span className="lead-card-field-label">Clinic</span>
                  <span className="lead-card-field-value">
                    {lead.clinicName ?? 'Clinic not assigned'}
                  </span>
                </div>
                {formatCreated(lead.createdAt) !== null && (
                  <div className="lead-card-field">
                    <span className="lead-card-field-label">Created</span>
                    <span className="lead-card-field-value">
                      {formatCreated(lead.createdAt)}
                    </span>
                  </div>
                )}
              </div>
              <span
                className={
                  lead.sameClinic
                    ? 'lead-badge lead-badge-same'
                    : 'lead-badge lead-badge-other'
                }
              >
                {lead.sameClinic ? 'Same clinic' : 'Other clinic'}
              </span>
              {linkEnabled && (
                <div className="lead-card-actions">
                  {/*
                    Primary CRM action: link this visit to the existing lead.
                    Styled prominent (filled .lead-use-button) -- the contact
                    "Use contact details" action on the form is the quiet
                    secondary. Behavior (onUseLead -> link) is unchanged.
                  */}
                  <button
                    type="button"
                    className="lead-use-button"
                    onClick={() => onUseLead?.(lead)}
                    disabled={!lead.sameClinic || actionPending}
                    aria-disabled={
                      !lead.sameClinic || actionPending ? 'true' : undefined
                    }
                    title={
                      !lead.sameClinic
                        ? 'Cross-clinic linking coming later'
                        : undefined
                    }
                  >
                    {lead.sameClinic
                      ? actionPending
                        ? 'Linking...'
                        : 'Use this lead'
                      : 'Cross-clinic linking coming later'}
                  </button>
                </div>
              )}
            </div>
          ))}
          {!linkEnabled && (
            <div className="lead-results-note" role="note">
              Review only. Linking or continuing a lead is not available yet.
            </div>
          )}
        </>
      )}
    </div>
  );
}
